import { ConnectorBadge, DynamicField } from '@/components/projects/workflows/shared/DynamicField'
import type { FormValues } from '@/components/projects/workflows/shared/dynamicFieldUtils'
import type { TriggerConnectorRef, TriggerInputField } from '@/types'
import { Loader2 } from 'lucide-react'
import type { UseFormReturn } from 'react-hook-form'

interface Props {
  projectId: string
  workflowDisplayName: string
  fields: TriggerInputField[]
  connectors: TriggerConnectorRef[]
  form: UseFormReturn<FormValues>
  isLoading: boolean
  isError: boolean
}

export function ScheduleInputsStep({ projectId, workflowDisplayName, fields, connectors, form, isLoading, isError }: Props) {
  const { register, watch, setValue, formState: { errors } } = form

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="ml-2 text-sm text-muted-foreground">Loading trigger schema...</span>
      </div>
    )
  }

  if (isError) {
    return (
      <p className="text-sm text-danger-foreground" role="alert" data-testid="schedule-inputs-error">
        The inputs of "{workflowDisplayName}" could not be loaded. The workflow may be inactive or invalid.
      </p>
    )
  }

  return (
    <div className="space-y-6" data-testid="schedule-inputs-step">
      <p className="text-sm text-muted-foreground">
        These values are stored with the schedule and used for every run of "{workflowDisplayName}".
      </p>

      {connectors.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {connectors.map(c => (
            <ConnectorBadge key={c.alias} connector={c} />
          ))}
        </div>
      )}

      {fields.length === 0 ? (
        <p className="text-sm text-foreground">This workflow has no trigger inputs.</p>
      ) : (
        fields.map(field => (
          <DynamicField
            key={field.name}
            field={field}
            projectId={projectId}
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
          />
        ))
      )}
    </div>
  )
}
